import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Tag, Image, Space, Card, Button, Switch } from 'antd';
import { EyeOutlined, DeleteOutlined, CheckOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ReadLaterItem } from '../../service/readLaterService';

dayjs.extend(relativeTime);
const { Text, Title } = Typography;

interface ReadLaterItemCardProps {
    item: ReadLaterItem;
    onMarkAsRead: (item: ReadLaterItem, isRead: boolean) => void;
    onRemove: (item: ReadLaterItem) => void;
    isMobile?: boolean;
}

const ReadLaterItemCard: React.FC<ReadLaterItemCardProps> = ({
    item,
    onMarkAsRead,
    onRemove,
    isMobile = false
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [imageError, setImageError] = useState(false);

    const timeSinceAdded = dayjs(item.added_at).fromNow(true);

    const handleClick = (event: React.MouseEvent) => {
        // Prevent default behavior and capture scroll position immediately
        event.preventDefault();
        
        // Save current scroll position immediately before navigation
        const currentScrollPosition = {
            x: window.scrollX,
            y: window.scrollY
        };
        
        // Force save the current scroll position to sessionStorage
        const stateKey = `readLater_${location.pathname}`;
        const currentState = sessionStorage.getItem(stateKey);
        if (currentState) {
            try {
                const parsedState = JSON.parse(currentState);
                parsedState.scrollPosition = currentScrollPosition;
                parsedState.timestamp = Date.now();
                sessionStorage.setItem(stateKey, JSON.stringify(parsedState));
            } catch (error) {
                // Silent fail
            }
        }
        
        // Also save to a separate key for backup
        try {
            sessionStorage.setItem(`${stateKey}_scroll_backup`, JSON.stringify({
                scrollPosition: currentScrollPosition,
                timestamp: Date.now()
            }));
        } catch (error) {
            // Silent fail
        }
        
        // Navigate after ensuring state is saved
        const navigateAfterSave = () => {
            if (item.cluster_id) {
                navigate(`/news/cluster/${item.cluster_id}`);
            } else {
                navigate(`/news/${item.article_id}`);
            }
        };

        // Use requestAnimationFrame for optimal timing
        requestAnimationFrame(() => {
            requestAnimationFrame(navigateAfterSave);
        });
    };

    const handleMarkAsRead = async (checked: boolean) => {
        onMarkAsRead(item, checked);
    };

    const handleRemove = async () => {
        onRemove(item);
    };

    return (
        <Card
            className="news-item-card"
            hoverable
            styles={{
                body: {
                    padding: isMobile ? '16px' : '20px'
                }
            }}
            style={{
                marginBottom: isMobile ? '12px' : '16px',
                cursor: 'pointer',
                borderRadius: isMobile ? '8px' : '12px',
                overflow: 'hidden',
                opacity: item.is_read ? 0.7 : 1,
            }}
            onClick={handleClick}
        >
            <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '16px' : '16px' 
            }}>
                {/* Image Section */}
                {item.image_url && !imageError && (
                    <div style={{ 
                        flexShrink: 0,
                        width: isMobile ? '100%' : '200px',
                        height: isMobile ? '180px' : '140px',
                        position: 'relative'
                    }}>
                        <Image
                            src={item.image_url}
                            alt={`Image for article: ${item.title || 'Untitled article'}`}
                            style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover', 
                                borderRadius: isMobile ? '6px' : '8px',
                                border: '1px solid #f0f0f0'
                            }}
                            fallback="https://via.placeholder.com/200x140?text=No+Image"
                            onError={() => setImageError(true)}
                            preview={false}
                        />
                    </div>
                )}
                
                {/* Content Section */}
                <div style={{ 
                    flex: 1, 
                    minWidth: 0
                }}>
                    {/* Header with read status and time */}
                    <div style={{ 
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        marginBottom: isMobile ? '16px' : '8px',
                        gap: isMobile ? '12px' : '0'
                    }}>
                        <Space size="small" wrap>
                            <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                {item.website_name || 'Unknown Source'}
                            </Text>
                            <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                Added {timeSinceAdded} ago
                            </Text>
                            {item.is_read && (
                                <Tag color="green" style={{ fontSize: '12px' }}>
                                    Read
                                </Tag>
                            )}
                        </Space>
                        <div style={{
                            display: 'flex',
                            justifyContent: isMobile ? 'flex-end' : 'flex-start',
                            alignItems: 'center',
                            width: isMobile ? '100%' : 'auto',
                            gap: isMobile ? '16px' : '8px'
                        }}>
                            {!isMobile && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    style={{
                                        color: '#8c8c8c',
                                        padding: '4px 8px',
                                        height: 'auto',
                                        fontSize: '12px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClick(e);
                                    }}
                                >
                                    View
                                </Button>
                            )}
                            <Switch
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<EyeInvisibleOutlined />}
                                checked={item.is_read}
                                onChange={handleMarkAsRead}
                                size="small"
                                onClick={(checked, e) => e?.stopPropagation()}
                            />
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{
                                    padding: '4px 8px',
                                    height: 'auto',
                                    fontSize: '12px'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove();
                                }}
                            >
                                {isMobile ? '' : 'Remove'}
                            </Button>
                        </div>
                    </div>
                    
                    {/* Title */}
                    <Title 
                        level={isMobile ? 5 : 4} 
                        style={{ 
                            margin: '0 0 8px 0', 
                            fontWeight: 'bold', 
                            lineHeight: '1.3',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                        ellipsis={{ rows: isMobile ? 3 : 2, tooltip: item.headline || item.title || 'No Title' }}
                    >
                        {item.headline || item.title || 'No Title'}
                    </Title>
                    
                    {/* Summary */}
                    {item.summary && (
                        <div
                            className="article-content"
                            style={{ 
                                margin: '0 0 12px 0',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: isMobile ? 4 : 3,
                                WebkitBoxOrient: 'vertical',
                                fontSize: isMobile ? '14px' : '15px',
                                lineHeight: '1.5'
                            }}
                            dangerouslySetInnerHTML={{ __html: item.summary }}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
};

export default ReadLaterItemCard;
